import { useDispatch, useSelector } from 'react-redux';

import * as SellerActions from '../../../store/seller/seller.actions';
import Global from '../../../store/global-selectors';
import { SHIPMENT_VOLUME_MODAL_OPENED } from '../../../constants/analytics-events';
import { PurchaseVolumeContent } from './purchase-volume-content';
import { UpgradeRequiredContent } from './upgrade-required-content';
import { ReadonlyContent } from './readonly-content';
import { useCanPurchaseShipmentVolume } from '../../purchase-shipment-volume/use-can-purchase-shipment-volume';
import { useShipmentUsageSummary } from '../../purchase-shipment-volume/use-shipment-usage-summary';
import { useTrackShipmentVolumeEvent } from './hooks/use-track-shipment-volume-event';

import type { PurchaseShipmentVolumeModalView } from './purchase-shipment-volume-modal';
import { useOnComponentMount } from '../../../hooks/use-on-component-mount';

type PurchaseShipmentVolumeModalContentProps = {
  onClose: () => void;
  onPurchaseSuccess?: () => void;
  isAtLimit: boolean;
  isHighLimitWarning: boolean;
  isMediumLimitWarning: boolean;
  usagePercentage: number;
  initialQuantity?: number | null;
  initialView?: PurchaseShipmentVolumeModalView;
};

export const PurchaseShipmentVolumeModalContent = ({
  onClose,
  onPurchaseSuccess,
  isAtLimit,
  isHighLimitWarning,
  isMediumLimitWarning,
  usagePercentage,
  initialQuantity,
  initialView,
}: PurchaseShipmentVolumeModalContentProps) => {
  const dispatch = useDispatch();
  const hasAccountManagementRole = useSelector(Global.selectUserhasAccountManagementRole);
  const planName = useSelector(Global.selectSellerPlanInfo)?.plan?.name ?? '';
  const { totalUsage, shipmentLimit, shipmentPeriodEnd } = useShipmentUsageSummary();
  const { canPurchaseVolume } = useCanPurchaseShipmentVolume();
  const trackShipmentVolumeEvent = useTrackShipmentVolumeEvent();

  useOnComponentMount(() => {
    dispatch(SellerActions.getShipmentUsage());
    trackShipmentVolumeEvent(SHIPMENT_VOLUME_MODAL_OPENED, {
      isReadOnly: !hasAccountManagementRole,
      canPurchaseVolume,
      initialView: initialView ?? 'purchase',
      initialQuantity: initialQuantity ?? undefined,
    });
  });

  if (!hasAccountManagementRole) {
    return (
      <ReadonlyContent
        isHighLimitWarning={isHighLimitWarning}
        isMediumLimitWarning={isMediumLimitWarning}
        isAtLimit={isAtLimit}
        onClose={onClose}
        planName={planName}
        shipmentLimit={shipmentLimit}
        totalUsage={totalUsage}
        usagePercentage={usagePercentage}
      />
    );
  }

  if (!canPurchaseVolume) {
    return (
      <UpgradeRequiredContent
        isAtLimit={isAtLimit}
        onClose={onClose}
        planName={planName}
        shipmentLimit={shipmentLimit}
        totalUsage={totalUsage}
        usagePercentage={usagePercentage}
      />
    );
  }

  return (
    <PurchaseVolumeContent
      initialQuantity={initialQuantity}
      initialView={initialView}
      isHighLimitWarning={isHighLimitWarning}
      isMediumLimitWarning={isMediumLimitWarning}
      isAtLimit={isAtLimit}
      onClose={onClose}
      onPurchaseSuccess={onPurchaseSuccess}
      planName={planName}
      shipmentLimit={shipmentLimit}
      shipmentPeriodEnd={shipmentPeriodEnd}
      totalUsage={totalUsage}
      usagePercentage={usagePercentage}
    />
  );
};
